import {useEffect, useRef} from "react";
import {useSearchParams} from "react-router-dom";
import {useListContext} from "../../contexts/ListContext";
import {useTheCellMapContext} from "../../contexts/TheCellMapContext";
import {URLParam} from "../../types/enums";
import {formatListDataTypeParam, parseListDataTypeParam} from "../../utils/urlParams";

const ListUrlStateController = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const {listState, setDataType, setDisplayedTableData, setSelectedRowModal} = useListContext();
  const {currentGene} = useTheCellMapContext();

  const initialParamsRef = useRef<URLSearchParams | null>(null);
  const didInitDataTypeRef = useRef(false);

  if (!initialParamsRef.current) {
    initialParamsRef.current = new URLSearchParams(searchParams);
  }

  const initialDataType = parseListDataTypeParam(initialParamsRef.current.get(URLParam.LIST_TYPE));

  /** Initialize list data type from URL once on page load. */
  useEffect(() => {
    if (didInitDataTypeRef.current) return;
    didInitDataTypeRef.current = true;

    if (!initialDataType || initialDataType === listState.dataType) return;
    // Keep behavior aligned with ListDataType switch logic.
    setDisplayedTableData(null);
    setSelectedRowModal(null);
    setDataType(initialDataType);
  }, [initialDataType, listState.dataType, setDataType, setDisplayedTableData, setSelectedRowModal]);

  /** Reflect current List View state into URL parameters without dropping unrelated params. */
  useEffect(() => {
    const params = new URLSearchParams(searchParams);

    params.set(URLParam.LIST_TYPE, formatListDataTypeParam(listState.dataType));

    if (currentGene) {
      params.set(URLParam.LIST_FOCUS, String(currentGene.value));
    } else if (!params.get(URLParam.GENES)) {
      params.delete(URLParam.LIST_FOCUS);
    }

    if (params.toString() === searchParams.toString()) return;
    setSearchParams(params, {replace: true});
  }, [currentGene, listState.dataType, searchParams, setSearchParams]);

  return null;
};

export default ListUrlStateController;
